<script setup lang="ts">
import { onMounted, onUnmounted, ref, getCurrentInstance } from 'vue';
import type { Feedback } from '@/shared/apollo-api/__generated__/types';
import { dateToLocale } from '../../helpers/date';
import { IconRating } from '@/features/ui-kit/icons';
import { MMText } from '@mm-frontend/mm-web-ui-kit';

interface Props {
  feedback: Feedback;
  alt?: string;
}

defineProps<Props>();

const emit = defineEmits<{
  'event-show-review': [];
}>();

const observer = ref<IntersectionObserver | null>(null);
const instance = getCurrentInstance();

const eventShow = () => {
  emit('event-show-review');
};

const getDate = (datePublished: Date) => {
  const published = new Date(datePublished);

  if (published.getFullYear() === new Date().getFullYear()) {
    return dateToLocale(published, { day: 'numeric', month: 'long' });
  } else {
    return dateToLocale(published, { day: 'numeric', month: 'long', year: 'numeric' });
  }
};

onMounted(() => {
  observer.value = new IntersectionObserver(([entry]) => {
    if (entry && entry.isIntersecting) {
      eventShow();
    }
  });

  if (instance?.vnode.el) {
    observer.value.observe(instance.vnode.el as Element);
  }
});

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});
</script>

<template>
  <div class="feedback-item-mobile">
    <div>
      <template v-for="n in 5" :key="`star-${n}`">
        <IconRating v-if="n <= feedback.rating" :size="24" color="rating-accented" />
        <IconRating v-else :size="24" class="star-empty" />
      </template>
    </div>

    <MMText font="Body M/Regular" color="tertiary">{{ getDate(feedback.datePublished) }}</MMText>
    <MMText font="Body M/Semibold">{{ feedback.customerName }}</MMText>
    <MMText font="Body M/Regular Long" class="content">{{ feedback.content }}</MMText>
  </div>
</template>

<style scoped lang="stylus">
.feedback-item-mobile
  display flex
  flex-direction column
  gap 8px

.content
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  overflow: hidden;

.star-empty
  color #DFE1E6
</style>
